import React, { useState, useCallback } from 'react';
import './HistoricoLeads.css';
import { apiFetch } from './api';
import { useRecurso } from './hooks/useRecurso';
import EstadoCarregando from './EstadoCarregando';
import EstadoVazio from './EstadoVazio';
import EstadoErro from './EstadoErro';

/**
 * Histórico de leads do tenant (PRD 22 / SEF-30, US-004). Consome GET /api/leads paginado;
 * todos os filtros vão para o backend (sem filtro local). Fluxo separado da Busca de Leads.
 */

const STATUS_OPCOES = [
  { valor: '', rotulo: 'Todos os status' },
  { valor: 'novo', rotulo: 'Novo' },
  { valor: 'qualificado', rotulo: 'Qualificado' },
  { valor: 'descartado', rotulo: 'Descartado' },
  { valor: 'convertido', rotulo: 'Convertido' },
];

const FONTE_OPCOES = [
  { valor: '', rotulo: 'Todas as fontes' },
  { valor: 'inpi', rotulo: 'INPI' },
  { valor: 'google', rotulo: 'Google Maps' },
  { valor: 'receita', rotulo: 'Receita Federal' },
];

const ROTULOS_STATUS = {
  novo: 'Novo', qualificado: 'Qualificado', descartado: 'Descartado', convertido: 'Convertido',
};

const PER_PAGE = 20;

function formatarData(iso) {
  if (!iso) {
    return '—';
  }
  return new Date(iso).toLocaleDateString('pt-BR');
}

function HistoricoLeads({ onNavegar }) {
  const [status, setStatus] = useState('');
  const [fonte, setFonte] = useState('');
  const [q, setQ] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [page, setPage] = useState(1);

  const buscar = useCallback(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('perPage', String(PER_PAGE));
    if (status) params.set('status', status);
    if (fonte) params.set('fonte', fonte);
    if (q.trim()) params.set('q', q.trim());
    if (dataInicio) params.set('dataInicio', dataInicio);
    if (dataFim) params.set('dataFim', dataFim);
    return apiFetch(`/api/leads?${params.toString()}`);
  }, [page, status, fonte, q, dataInicio, dataFim]);

  const { dados, carregando, erro, recarregar } = useRecurso(buscar, [page, status, fonte, q, dataInicio, dataFim]);
  const leads = dados?.data ?? [];
  const metadata = dados?.metadata ?? { total: 0, page: 1, perPage: PER_PAGE, totalPages: 0 };

  // Mudar filtro volta para a página 1 (evita pedir página inexistente do novo conjunto).
  const aoFiltrar = (setter) => (valor) => {
    setter(valor);
    setPage(1);
  };

  return (
    <div className="historico-leads-container">
      <div className="historico-leads-header">
        <div>
          <h1>Histórico de Leads</h1>
          <p className="descricao">Leads pesquisados e convertidos pelo escritório</p>
        </div>
        <button type="button" className="historico-link-busca" onClick={() => onNavegar('leads')}>
          <i className="fa-solid fa-magnifying-glass-chart" aria-hidden="true"></i> Buscar novos leads
        </button>
      </div>

      <div className="historico-filtros">
        <select aria-label="Status" value={status} onChange={(e) => aoFiltrar(setStatus)(e.target.value)}>
          {STATUS_OPCOES.map((o) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
        </select>
        <select aria-label="Fonte" value={fonte} onChange={(e) => aoFiltrar(setFonte)(e.target.value)}>
          {FONTE_OPCOES.map((o) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
        </select>
        <input
          type="text"
          aria-label="Buscar por nome"
          placeholder="Buscar por nome..."
          value={q}
          onChange={(e) => aoFiltrar(setQ)(e.target.value)}
        />
        <input type="date" aria-label="Data início" value={dataInicio}
          onChange={(e) => aoFiltrar(setDataInicio)(e.target.value)} />
        <input type="date" aria-label="Data fim" value={dataFim}
          onChange={(e) => aoFiltrar(setDataFim)(e.target.value)} />
      </div>

      {carregando ? (
        <EstadoCarregando mensagem="Carregando histórico..." />
      ) : erro ? (
        <EstadoErro mensagem="Não foi possível carregar o histórico." onRetry={recarregar} />
      ) : leads.length === 0 ? (
        <EstadoVazio
          titulo="Nenhum lead encontrado"
          descricao="Ajuste os filtros ou busque novos leads."
        />
      ) : (
        <>
          <div className="historico-lista">
            {leads.map((lead) => (
              <div className="historico-item" key={lead.id}>
                <div className="historico-item-info">
                  <h3>{lead.nome}</h3>
                  <span className="historico-item-meta">
                    {FONTE_OPCOES.find((f) => f.valor === lead.fonte)?.rotulo || lead.fonte}
                    {' · '}{formatarData(lead.criadoEm)}
                    {lead.identificadorExterno ? ` · ${lead.identificadorExterno}` : ''}
                  </span>
                  {lead.status === 'descartado' && lead.motivoStatus && (
                    <span className="historico-item-motivo">Motivo: {lead.motivoStatus}</span>
                  )}
                </div>
                <div className="historico-item-acoes">
                  <span className={`lead-status-badge status-${lead.status || 'novo'}`}>
                    {ROTULOS_STATUS[lead.status] || 'Novo'}
                  </span>
                  {lead.status === 'convertido' && lead.clienteId && onNavegar && (
                    <button
                      type="button"
                      className="historico-ver-cliente"
                      onClick={() => onNavegar('detalhes-cliente', { clienteId: lead.clienteId })}
                    >
                      Ver cliente
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="historico-paginacao">
            <button type="button" disabled={metadata.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Anterior
            </button>
            <span>Página {metadata.page} de {Math.max(1, metadata.totalPages)} ({metadata.total} no total)</span>
            <button
              type="button"
              disabled={metadata.page >= metadata.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Próxima
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default HistoricoLeads;
